import { faChevronLeft, faChevronRight, faSearchPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";
import { ReactImageGalleryItem } from "react-image-gallery";

import { blogsProfileUrl } from "@/mk2/apps/blogs/urls";
import { Props } from "@/mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotosPage";
import { strollersAllReviewsPhotosUrl, strollersReviewsUrl } from "@/mk2/apps/strollers/urls";
import { AvatarImg } from "@/mk2/components/AvatarImg";
import { Dot } from "@/mk2/components/Dot";
import { ImageGalleryLoadable } from "@/mk2/components/ImageGalleryLoadable";
import { Link } from "@/mk2/components/Link";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { resizeForCover } from "@/mk2/helpers/photos";
import { dateLocal } from "@/mk2/helpers/timeformat";
import { url } from "@/mk2/helpers/urls";
import { StrollerUserReviewPhotoEntity } from "@/mk2/schemas";
import { PHOTO_FROM_USER_RATING } from "@/mk/autogenerated/translations/StrollerAllReviewsPhotos.3e194c8af3a2cf62ba6e73247dccd95a";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./StrollerAllReviewsPhotos.mscss";
import "./StrollerAllReviewsPhotos.scss";

export class StrollerAllReviewsPhotos extends React.Component<Props> {
    public componentDidMount() {
        const { onCheckHideThumbnails } = this.props;
        if (typeof window !== "undefined") {
            // run on mount if not SSR
            onCheckHideThumbnails();
        }
    }

    public render() {
        const { loadingState, stroller } = this.props;

        return (
            <div className={styles.StrollerAllReviewsPhotos}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderSuccess={this.renderSuccess}
                    hasData={!!stroller}
                />
            </div>
        );
    }

    private renderSuccess = () => {
        const { strollerReviewsPhotos, strollerReviewsPhoto, stroller, hideThumbnails, thumbsKey } =
            this.props;

        const currentIndex: number = strollerReviewsPhotos.findIndex(
            (p) => p.id === strollerReviewsPhoto.id,
        );
        const review = strollerReviewsPhoto.review;

        const showThumbnails = !hideThumbnails;

        const galleryPhotos: ReactImageGalleryItem[] = strollerReviewsPhotos.map((p) => {
            const thumbData = showThumbnails
                ? {
                      thumbnail: photoUrl(p.photoFile, "s150x150"),
                      thumbnailHeight: resizeForCover(p.photoFile, 42).height,
                      thumbnailWidth: resizeForCover(p.photoFile, 42).width,
                  }
                : {};
            return {
                original: photoUrl(p.photoFile, "s720x720"),
                originalHeight: p.photoFile.height,
                originalWidth: p.photoFile.width,
                loading: "lazy",
                ...thumbData,
            };
        });

        return (
            <>
                <div className={styles.StrollerAllReviewsPhotos__gallery}>
                    {!!review && (
                        <div
                            className={cx(
                                styles.StrollerAllReviewsPhotos__gallery__info,
                                showThumbnails &&
                                    styles.StrollerAllReviewsPhotos__gallery__info__topOffset,
                            )}
                        >
                            <AvatarImg size={36} user={review.user} />
                            <div className={styles.StrollerAllReviewsPhotos__gallery__info__card}>
                                <span
                                    className={
                                        styles.StrollerAllReviewsPhotos__gallery__info__card__nameAndDate
                                    }
                                >
                                    <Link
                                        to={url(blogsProfileUrl, {
                                            username: review.user.username,
                                        })}
                                        className={
                                            styles.StrollerAllReviewsPhotos__gallery__info__link
                                        }
                                    >
                                        <b>{review.user.username}</b>
                                    </Link>
                                    <Dot spaces={1} />
                                    <span>{dateLocal(review.createdTime, false)}</span>
                                </span>
                                <Link
                                    to={url(
                                        strollersReviewsUrl,
                                        { slug: stroller.slug },
                                        {},
                                        `review-${review.id}`,
                                    )}
                                >
                                    {PHOTO_FROM_USER_RATING}
                                </Link>
                            </div>
                        </div>
                    )}
                    {/* when changing showThumbnails, ImageGalleryLoadable need to be re-rendered */}
                    {hideThumbnails !== null ? (
                        <ImageGalleryLoadable
                            key={`gal-thumbs-${thumbsKey}`}
                            items={galleryPhotos}
                            showIndex
                            showThumbnails={showThumbnails}
                            thumbnailPosition={showThumbnails ? "top" : undefined}
                            autoPlay={false}
                            showPlayButton={false}
                            showFullscreenButton={false}
                            slideDuration={500}
                            startIndex={currentIndex}
                            onSlide={this.onSlide}
                            renderLeftNav={this.renderLeftNav}
                            renderRightNav={this.renderRightNav}
                            renderCustomControls={this.renderCustomControls}
                        />
                    ) : (
                        <LoadingPlaceholder width="100%" height={600} />
                    )}
                </div>
            </>
        );
    };

    private renderLeftNav = (onClick, disabled) => {
        return (
            <div
                key="prev"
                className={styles.StrollerAllReviewsPhotos__prev}
                onClick={disabled ? undefined : onClick}
            >
                <FontAwesomeIcon
                    icon={faChevronLeft}
                    className={styles.StrollerAllReviewsPhotos__prev__icon}
                />
            </div>
        );
    };

    private renderRightNav = (onClick, disabled) => {
        return (
            <div
                key="next"
                className={styles.StrollerAllReviewsPhotos__next}
                onClick={disabled ? undefined : onClick}
            >
                <FontAwesomeIcon
                    icon={faChevronRight}
                    className={styles.StrollerAllReviewsPhotos__next__icon}
                />
            </div>
        );
    };

    private renderCustomControls = () => {
        const { photoId, strollerReviewsPhotos } = this.props;
        const photo: StrollerUserReviewPhotoEntity = strollerReviewsPhotos.find(
            (p) => p.photoFile.id === photoId,
        );
        const showMagnifier = photo?.photoFile?.versions?.includes("s1600x1600");
        if (!showMagnifier) {
            return null;
        }

        return (
            <div className={styles.StrollerAllReviewsPhotos__controls}>
                {showMagnifier && (
                    <a
                        data-google-interstitial="false"
                        key="magnifier"
                        href={photoUrl(photo.photoFile, "s1600x1600")}
                        target="_blank"
                        className={styles.StrollerAllReviewsPhotos__controls__control}
                        rel="noopener"
                    >
                        <FontAwesomeIcon icon={faSearchPlus} />
                    </a>
                )}
            </div>
        );
    };

    private onSlide = (currentIndex: number) => {
        const {
            routeNavigate,
            strollerReviewsPhotos,
            routeParams: { slug },
        } = this.props;
        const photo = strollerReviewsPhotos[currentIndex];
        routeNavigate(
            url(strollersAllReviewsPhotosUrl, {
                slug,
                reviewId: photo.review.id,
                photoId: photo.photoFile.id,
            }),
            { replace: true },
        );
    };

    private load = () => {
        const { strollerSlug, photoId, onLoad } = this.props;
        onLoad(strollerSlug, photoId);
    };
}
